import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ImageLoader from '../components/ImageLoader';

interface TourPackage {
  id: number;
  name: string;
  description: string;
  duration: string;
  price: number;
  highlights: string[];
  imageIndex: number;
  isPartner?: boolean;
  partnerName?: string;
  googleMapsUrl?: string;
  hideImage?: boolean;
}

const CustomImage = ({ src, alt, className }: { src: string, alt: string, className?: string }) => {
  return (
    <img 
      src={`${process.env.PUBLIC_URL}/${src.replace(/^\//, '')}`} 
      alt={alt} 
      className={className || ''} 
    />
  );
};

const FarmTours: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<TourPackage | null>(null);

  const tourPackages: TourPackage[] = [
    {
      id: 1,
      name: "Farm Discovery Tour",
      description: "A guided tour of our organic farm where you'll learn about traditional Cambodian farming practices, see exotic herbs and vegetables growing, and understand the importance of sustainability in our cuisine.",
      duration: "2 hours",
      price: 35,
      highlights: [
        "Explore our organic vegetable and herb gardens",
        "Learn about traditional Cambodian farming techniques",
        "Sample freshly picked produce",
        "Meet our local farmers and hear their stories"
      ],
      imageIndex: 22
    },
    {
      id: 2,
      name: "Farm-to-Table Experience",
      description: "The complete Tuk Tuk experience! Tour our farm, pick your own ingredients, and join our chefs for a cooking demonstration. Finally, enjoy the meal you helped create with a glass of local rice wine or fruit juice.",
      duration: "4 hours",
      price: 45,
      highlights: [
        "Full farm tour with ingredient harvesting",
        "Hands-on cooking class with our chef",
        "Learn to prepare a traditional Cambodian dish",
        "Enjoy your creation for lunch with drinks included",
        "Take home recipe cards"
      ],
      imageIndex: 21
    },
    {
      id: 3,
      name: "Sunset Farm Dinner",
      description: "Experience the serenity of our farm at sunset. After a short guided tour, enjoy a specially prepared multi-course dinner served outdoors among the gardens, showcasing the day's freshest ingredients.",
      duration: "3 hours",
      price: 35,
      highlights: [
        "Evening guided tour of the farm",
        "Spectacular sunset views over the countryside",
        "Four-course dinner prepared with the day's harvest",
        "Traditional Cambodian music performance during dinner"
      ],
      imageIndex: 27
    },
    {
      id: 11,
      name: "Ultimate Farm Experience Package",
      description: "Experience the complete farm-to-table journey in this full-day package. Start with a morning Farm Discovery Tour, continue with our hands-on Farm-to-Table Experience, and end the day with our tranquil Sunset Farm Dinner. This all-inclusive package is perfect for those wanting to immerse themselves in Cambodian culinary traditions.",
      duration: "Full day (9:00 AM - 8:00 PM)",
      price: 100,
      highlights: [
        "Morning guided tour of our organic farm",
        "Learn about traditional Cambodian farming techniques",
        "Harvest your own ingredients",
        "Hands-on cooking class with our chef",
        "Prepare and enjoy your own Cambodian meal for lunch",
        "Evening guided tour at sunset",
        "Multi-course dinner featuring the day's harvest",
        "Live traditional Cambodian music during dinner"
      ],
      imageIndex: 27
    }
  ];

  const partnerTourPackages: TourPackage[] = [
    {
      id: 4,
      name: "Angkor Wat Tour",
      description: "Explore the magnificent temples of Angkor, a UNESCO World Heritage site and one of the most important archaeological sites in Southeast Asia. Our expert guides will take you through the history and significance of these ancient structures.",
      duration: "Full day",
      price: 45,
      highlights: [
        "Visit the iconic Angkor Wat temple complex",
        "Explore Ta Prohm, famous for trees growing out of the ruins",
        "Discover Bayon Temple with its many carved faces",
        "Comfortable transportation and cold water included",
        "English-speaking knowledgeable guide"
      ],
      imageIndex: 25,
      isPartner: true,
      partnerName: "KNR Travel And Tour",
      googleMapsUrl: "https://maps.app.goo.gl/b13RgRtajwT1jdU99"
    },
    {
      id: 5,
      name: "Kulen Waterfall Adventure",
      description: "Journey to the sacred Phnom Kulen mountain, considered the birthplace of the ancient Khmer Empire. Swim in the refreshing natural pools beneath beautiful waterfalls and visit the river of 1000 lingas.",
      duration: "Full day",
      price: 70,
      highlights: [
        "Visit the River of a Thousand Lingas with sacred carvings",
        "Swim in natural pools beneath stunning waterfalls",
        "See the giant reclining Buddha at the mountaintop temple",
        "Enjoy a picnic lunch in a beautiful natural setting",
        "Round-trip transportation from Siem Reap"
      ],
      imageIndex: 27,
      isPartner: true,
      partnerName: "KNR Travel And Tour",
      googleMapsUrl: "https://maps.app.goo.gl/b13RgRtajwT1jdU99"
    },
    {
      id: 6,
      name: "Phnom Penh City Tour",
      description: "Discover Cambodia's capital city with this comprehensive tour of Phnom Penh's most significant historical and cultural sites. Learn about Cambodia's complex history and vibrant present.",
      duration: "2-3 days",
      price: 120,
      highlights: [
        "Visit the somber Killing Fields and Tuol Sleng Genocide Museum",
        "Explore the magnificent Royal Palace and Silver Pagoda",
        "Experience the bustling Central Market",
        "Guided tour of the National Museum of Cambodia",
        "Round-trip transportation and accommodation included"
      ],
      imageIndex: 24,
      isPartner: true,
      partnerName: "KNR Travel And Tour",
      googleMapsUrl: "https://maps.app.goo.gl/b13RgRtajwT1jdU99"
    },
    {
      id: 10,
      name: "Shooting Range Experience",
      description: "Experience the thrill of target shooting with various weapons in a safe, controlled environment. Professional instructors provide guidance for beginners and experts alike. The price shown is the starting price. Please contact us for more information.",
      duration: "2-3 hours",
      price: 60,
      highlights: [
        "Professional safety briefing and instruction",
        "Range of weapons available for shooting",
        "Experienced instructors and guides",
        "All equipment provided including ear and eye protection",
        "Convenient transportation from Siem Reap"
      ],
      imageIndex: 0,
      isPartner: true,
      partnerName: "KNR Travel And Tour",
      googleMapsUrl: "https://maps.app.goo.gl/b13RgRtajwT1jdU99",
      hideImage: true
    },
    {
      id: 7,
      name: "Siem Reap to Laos: Bus Transfer",
      description: "Direct bus service from Siem Reap to Laos. This is a transportation service only, providing a comfortable and reliable way to travel between the two destinations.",
      duration: "Varies by destination",
      price: 30,
      highlights: [
        "Comfortable air-conditioned bus transportation",
        "Professional driver",
        "Direct route to major destinations in Laos",
        "Water included during journey",
        "Help with border crossing procedures"
      ],
      imageIndex: 13,
      isPartner: true,
      partnerName: "KNR Travel And Tour",
      googleMapsUrl: "https://maps.app.goo.gl/b13RgRtajwT1jdU99"
    },
    {
      id: 8,
      name: "Siem Reap to Bangkok: Bus Transfer",
      description: "Direct bus service from Siem Reap to Bangkok. This is a transportation service only, not a guided tour, providing a convenient way to travel between Cambodia and Thailand.",
      duration: "8-10 hours",
      price: 25,
      highlights: [
        "Comfortable air-conditioned bus transportation",
        "Professional driver",
        "Direct route to Bangkok",
        "Water and snack provided",
        "Assistance with border crossing formalities"
      ],
      imageIndex: 14,
      isPartner: true,
      partnerName: "KNR Travel And Tour",
      googleMapsUrl: "https://maps.app.goo.gl/b13RgRtajwT1jdU99"
    },
    {
      id: 9,
      name: "Siem Reap to Vietnam: Bus Transfer",
      description: "Direct bus service from Siem Reap to various destinations in Vietnam. This is a transportation service only, not a tour package, offering a reliable way to travel between Cambodia and Vietnam.",
      duration: "Varies by destination",
      price: 35,
      highlights: [
        "Comfortable air-conditioned bus transportation",
        "Professional driver",
        "Routes to major Vietnamese cities",
        "Water provided during journey",
        "Assistance with visa and border procedures"
      ],
      imageIndex: 12,
      isPartner: true,
      partnerName: "KNR Travel And Tour",
      googleMapsUrl: "https://maps.app.goo.gl/b13RgRtajwT1jdU99"
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-16 bg-tuktuk-beige bg-opacity-10">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <section className="relative h-96 mb-16 rounded-2xl overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center z-10">
            <div className="text-center px-4">
              <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 drop-shadow-lg">
                Farm Tours & Adventures
              </h1>
              <p className="text-xl text-white max-w-2xl mx-auto drop-shadow-lg">
                Discover the Origins of Your Meal and Explore the Treasures of Cambodia
              </p>
            </div>
          </div>
          <div className="absolute inset-0">
            <ImageLoader 
              index={3} 
              alt="Our farm in Cambodia" 
              className="h-full w-full object-cover"
            />
          </div>
        </section>

        {/* Introduction */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-display font-bold text-tuktuk-brown mb-6">
              Explore Cambodia's Natural Beauty & Rich Culture
            </h2>
            <p className="text-lg mb-6">
              Embark on an enlightening journey through our farm and beyond. Experience the authentic 
              Cambodian countryside, learn about traditional farming methods, and explore the stunning 
              temples and natural wonders of the region.
            </p>
            <p className="text-lg mb-8">
              We offer our own farm tours directly, and have partnered with KNR Travel And Tour to provide 
              you with memorable adventures throughout Cambodia and neighboring countries. From the iconic 
              temples of Angkor Wat to the stunning waterfalls of Kulen Mountain, and even extended journeys 
              to Vietnam, Laos, and Thailand.
            </p>
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl">
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <div className="w-12 h-12 bg-tuktuk-green rounded-full flex items-center justify-center text-white mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-tuktuk-brown mb-2">Educational</h3>
                  <p className="text-sm">Learn about traditional Cambodian farming techniques and cultural heritage</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <div className="w-12 h-12 bg-tuktuk-red rounded-full flex items-center justify-center text-white mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-tuktuk-brown mb-2">Interactive</h3>
                  <p className="text-sm">Hands-on experiences and immersive cultural adventures</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <div className="w-12 h-12 bg-tuktuk-brown rounded-full flex items-center justify-center text-white mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-tuktuk-brown mb-2">Memorable</h3>
                  <p className="text-sm">Create lasting memories and deeper appreciation for Cambodian culture</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Farm Tour Packages */}
        <section className="mb-16">
          <h2 className="text-3xl font-display font-bold text-center text-tuktuk-brown mb-4">
            Our Farm Experiences
          </h2>
          <p className="text-center max-w-2xl mx-auto mb-10">
            Discover the source of our fresh ingredients and connect with Cambodia's agricultural heritage
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tourPackages.map(pkg => (
              <div 
                key={pkg.id} 
                className="bg-white rounded-lg overflow-hidden shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 cursor-pointer"
                onClick={() => setSelectedPackage(pkg)}
              >
                <div className="h-64 w-full mb-4 rounded-t-lg overflow-hidden">
                  {pkg.hideImage ? (
                    <div className="w-full h-full bg-tuktuk-beige flex items-center justify-center">
                      <span className="text-tuktuk-brown font-bold text-center px-4">Images Available Upon Request</span>
                    </div>
                  ) : pkg.id === 1 ? (
                    <CustomImage 
                      src="farm-discovery.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 2 ? (
                    <CustomImage 
                      src="farm-to-table.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 3 ? (
                    <CustomImage 
                      src="sunset-farm-dinner.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 4 ? (
                    <CustomImage 
                      src="angkor-wat-tour.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 5 ? (
                    <CustomImage 
                      src="kulen-waterfall.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 6 ? (
                    <CustomImage 
                      src="phnom-penh-tour.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 11 ? (
                    <CustomImage 
                      src="ultimate-farm-experience.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : (
                    <ImageLoader 
                      index={pkg.imageIndex} 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-display font-bold text-tuktuk-brown">{pkg.name}</h3>
                    <span className="text-tuktuk-red font-bold">${pkg.price}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description.substring(0, 100)}...</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Duration: {pkg.duration}</span>
                    <button 
                      className="text-tuktuk-green font-medium hover:text-tuktuk-brown transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPackage(pkg);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Partner Tour Packages */}
        <section className="mb-16">
          <h2 className="text-3xl font-display font-bold text-center text-tuktuk-brown mb-4">
            Regional Adventures
          </h2>
          <p className="text-center max-w-2xl mx-auto mb-10">
            In partnership with KNR Travel And Tour, we offer these exciting adventures to explore Cambodia and beyond
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partnerTourPackages.map(pkg => (
              <div 
                key={pkg.id} 
                className="bg-white rounded-lg overflow-hidden shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 cursor-pointer"
                onClick={() => setSelectedPackage(pkg)}
              >
                <div className="h-64 w-full mb-4 rounded-t-lg overflow-hidden">
                  {pkg.hideImage ? (
                    <div className="w-full h-full bg-tuktuk-beige flex items-center justify-center">
                      <span className="text-tuktuk-brown font-bold text-center px-4">Images Available Upon Request</span>
                    </div>
                  ) : pkg.id === 4 ? (
                    <CustomImage 
                      src="angkor-wat-tour.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 5 ? (
                    <CustomImage 
                      src="kulen-waterfall.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 6 ? (
                    <CustomImage 
                      src="phnom-penh-tour.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 9 ? (
                    <CustomImage 
                      src="vietnam-bus.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : pkg.id === 11 ? (
                    <CustomImage 
                      src="sunset.jpg" 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  ) : (
                    <ImageLoader 
                      index={pkg.imageIndex} 
                      alt={pkg.name} 
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-display font-bold text-tuktuk-brown">{pkg.name}</h3>
                    <span className="text-tuktuk-red font-bold">${pkg.price}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description.substring(0, 100)}...</p>
                  <div className="flex flex-col space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Duration: {pkg.duration}</span>
                      <button 
                        className="text-tuktuk-green font-medium hover:text-tuktuk-brown transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPackage(pkg);
                        }}
                      >
                        View Details
                      </button>
                    </div>
                    {pkg.isPartner && (
                      <div className="text-xs text-gray-500 italic">
                        Provided by {pkg.partnerName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Booking Information */}
        <section className="bg-white p-8 rounded-lg shadow-lg mb-16">
          <h2 className="text-3xl font-display font-bold text-center text-tuktuk-brown mb-6">
            Booking Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-4">How to Book</h3>
              <p className="mb-3">
                Tours are available daily with morning and afternoon sessions. Advance reservations are required.
              </p>
              <p className="mb-3">
                To book a tour, please contact us directly via WhatsApp at +81 70-4476-1574 or 
                visit our partner's office at the address below:
              </p>
              <div className="bg-tuktuk-beige bg-opacity-20 p-4 rounded-lg mb-3">
                <p className="font-semibold">KNR Travel And Tour</p>
                <p>Phone: 017 236 123</p>
                <p className="mb-2">"Never Try Never Know"</p>
                <a 
                  href="https://maps.app.goo.gl/b13RgRtajwT1jdU99" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  View on Google Maps
                </a>
              </div>
              <p>
                For groups larger than 8 people, please email us for special arrangements.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-4">What to Bring</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>Comfortable walking shoes</li>
                <li>Hat and sunscreen</li>
                <li>Camera</li>
                <li>Reusable water bottle (we provide filtered water)</li>
                <li>Small backpack for personal items</li>
                <li>For multi-day tours: appropriate clothing and essentials</li>
                <li>Your curiosity and sense of adventure!</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <h2 className="text-3xl font-display font-bold text-tuktuk-brown mb-6">
            Ready for an Unforgettable Adventure?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Join us for an journey from farm to table or explore the wonders of Cambodia and beyond.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="https://wa.me/817044761574" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-block bg-green-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-600 transition duration-300 transform hover:-translate-y-1 flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp Booking
            </a>
            <Link 
              to="/contact" 
              className="inline-block bg-tuktuk-green hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:-translate-y-1"
            >
              Book Your Tour
            </Link>
          </div>
        </section>
      </div>

      {/* Tour Package Modal */}
      {selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPackage(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="relative h-64 md:h-80 rounded-xl overflow-hidden mb-6">
              {selectedPackage.id === 1 ? (
                <CustomImage 
                  src="farm-discovery.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : selectedPackage.id === 2 ? (
                <CustomImage 
                  src="farm-to-table.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : selectedPackage.id === 3 ? (
                <CustomImage 
                  src="sunset-farm-dinner.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : selectedPackage.id === 4 ? (
                <CustomImage 
                  src="angkor-wat-tour.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : selectedPackage.id === 5 ? (
                <CustomImage 
                  src="kulen-waterfall.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : selectedPackage.id === 6 ? (
                <CustomImage 
                  src="phnom-penh-tour.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : selectedPackage.id === 11 ? (
                <CustomImage 
                  src="ultimate-farm-experience.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : selectedPackage.id === 9 ? (
                <CustomImage 
                  src="vietnam-bus.jpg" 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              ) : (
                <ImageLoader 
                  index={selectedPackage.imageIndex || 0} 
                  alt={selectedPackage.name} 
                  className="h-full w-full object-cover"
                />
              )}
              <button 
                className="absolute top-2 right-2 bg-black bg-opacity-50 text-white w-8 h-8 rounded-full flex items-center justify-center"
                onClick={() => setSelectedPackage(null)}
              >
                &times;
              </button>
            </div>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-3xl font-display font-bold text-tuktuk-brown">{selectedPackage.name}</h2>
                <span className="text-2xl text-tuktuk-red font-bold">${selectedPackage.price}/person</span>
              </div>
              {selectedPackage.isPartner && (
                <div className="mb-4 text-sm bg-tuktuk-beige bg-opacity-20 p-2 rounded inline-block">
                  Provided by {selectedPackage.partnerName}
                </div>
              )}
              <p className="text-gray-700 mb-6">{selectedPackage.description}</p>
              <div className="mb-6">
                <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-3">Tour Highlights</h3>
                <ul className="list-disc pl-5 space-y-2">
                  {selectedPackage.highlights.map((highlight, index) => (
                    <li key={index} className="text-gray-700">{highlight}</li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <span className="text-gray-600">Duration: {selectedPackage.duration}</span>
                <div className="flex flex-wrap gap-2">
                  {selectedPackage.googleMapsUrl && (
                    <a 
                      href={selectedPackage.googleMapsUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300 flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      View on Maps
                    </a>
                  )}
                  {selectedPackage.isPartner ? (
                    <a 
                      href="tel:+855017236123" 
                      className="bg-tuktuk-brown hover:bg-yellow-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300"
                    >
                      Call to Book
                    </a>
                  ) : (
                    <Link 
                      to="/contact" 
                      className="bg-tuktuk-green hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300"
                    >
                      Book This Tour
                    </Link>
                  )}
                  <a 
                    href="https://wa.me/855017236123" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300 flex items-center"
                  >
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmTours; 